<script module lang="ts">
  import type { ResolvedRef as Resolved } from '../lib/state/ref/fetch';
  /**
   * Resolutions handed to the page that opens at a history's one URL,
   * which the address bar was just rewritten to (by its locator's text):
   * that page shows at once, asking GitHub nothing again.
   */
  const handed = new Map<string, Resolved>();
  const locatorKey = (l: { owner: string; repo: string; spec: string; path?: string }) => `${l.owner}/${l.repo}/${l.spec}${l.path === undefined ? '' : '?' + l.path}`;
</script>

<script lang="ts">
  // A branch history's address (docs/design.md "Branch histories"): the
  // route names a locator (`commits/<spec>`), which only the repository
  // can split into a ref and a path, so this resolves it first
  // (state/ref/fetch.ts `resolveRef`), then shows the resolved subject's
  // page (RefPage). A spec no branch, tag or commit matches is "Branch not
  // found". Once resolved, the address bar is rewritten to the
  // subject's one URL (router.ts subjectPath, `?under=` for a path), so a
  // GitHub-shaped `commits/<ref>/<path>` and the canonical URL are one page.
  import { failureOf, type Failure } from '../lib/api/failure';
  import { oneStep } from '../lib/loading/steps';
  import LoadingGate from '../lib/loading/LoadingGate.svelte';
  import Rail from '../lib/components/Rail.svelte';
  import { repoPath, subjectPath, UNDER_PARAM, VIEW_PARAM, type RefRoute } from '../lib/router';
  import { nav } from '../lib/nav.svelte';
  import { RefNotFound, resolveRef, type ResolvedRef } from '../lib/state/ref/fetch';
  import type { RefLocator } from '../lib/subject';
  import RefPage from './RefPage.svelte';

  interface Props {
    route: RefRoute;
    /** How a locator is resolved: against GitHub unless given (tests). */
    resolve?: (locator: RefLocator, signal: AbortSignal) => Promise<ResolvedRef>;
  }
  let { route, resolve = (locator, signal) => resolveRef(locator, { priority: 'visible', signal }) }: Props = $props();

  type Resolution = { is: 'resolving' } | { is: 'resolved'; ref: ResolvedRef } | { is: 'notfound'; why: string } | { is: 'failed'; failure: Failure };
  let at: Resolution = $state({ is: 'resolving' });
  // The locator is the route's for the page's life (App keys this view by it).
  // svelte-ignore state_referenced_locally
  const locator = route.locator;
  const abort = new AbortController();
  $effect(() => () => abort.abort());

  /** The subject's one URL, keeping the query's other parameters (a Files filter, a range); null when the route is it already. */
  function canonical(ref: ResolvedRef): string | null {
    const s = ref.subject;
    if (locator.spec === s.ref && (locator.path ?? null) === s.path) return null;
    const url = new URL(subjectPath(s, route.view), 'http://app');
    for (const [k, v] of nav.query) if (k !== UNDER_PARAM && k !== VIEW_PARAM) url.searchParams.append(k, v);
    return url.pathname + url.search;
  }

  function run() {
    const given = handed.get(locatorKey(locator));
    handed.delete(locatorKey(locator));
    if (given) {
      at = { is: 'resolved', ref: given };
      return;
    }
    at = { is: 'resolving' };
    resolve(locator, abort.signal).then(
      (ref) => {
        const to = canonical(ref);
        if (to) {
          handed.set(locatorKey({ owner: locator.owner, repo: locator.repo, spec: ref.subject.ref, ...(ref.subject.path === null ? {} : { path: ref.subject.path }) }), ref);
          // At most a few are ever waiting (one per rewrite not yet opened): the oldest go.
          for (const k of [...handed.keys()].slice(0, Math.max(0, handed.size - 4))) handed.delete(k);
          nav.go(to, { replace: true, keepScroll: true });
        }
        at = { is: 'resolved', ref };
      },
      (e) => {
        if (abort.signal.aborted) return;
        at = e instanceof RefNotFound ? { is: 'notfound', why: e.message } : { is: 'failed', failure: failureOf(e) };
      },
    );
  }
  run();

  const label = `${locator.owner}/${locator.repo} / ${locator.spec}${locator.path === undefined ? '' : ' · ' + locator.path}`;
</script>

{#if at.is === 'resolved'}
  <RefPage resolved={at.ref} view={route.view} />
{:else}
  <div class="shell">
    <Rail />
    <main>
      <LoadingGate
        phase={at.is === 'resolving' ? 'loading' : at.is === 'failed' ? 'failed' : 'ready'}
        view={oneStep(label, `Finding ${locator.spec}`, at.is === 'resolving' ? 'loading' : at.is === 'failed' ? { failure: at.failure, what: 'this branch' } : 'ready')}
        onretry={run}
      >
        {#if at.is === 'notfound'}
          <div class="center-msg">
            <h1>Branch not found</h1>
            <p class="muted">{at.why} Changeling sees exactly what your GitHub account can see.</p>
            <a class="btn" href={repoPath(locator.owner, locator.repo)}>Go to {locator.owner}/{locator.repo}</a>
          </div>
        {/if}
      </LoadingGate>
    </main>
  </div>
{/if}
